'use client';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import React from 'react';

interface BreadcrumbItem {
  readonly name: string;
  readonly path: string;
}

interface BreadcrumbsProps {
  readonly currentPageName?: string;
}

const Breadcrumbs: React.FC<BreadcrumbsProps> = ({ currentPageName }) => {
  const pathname = usePathname();
  const pathSegments = pathname?.split('/').filter((segment) => segment !== '') || [];

  const capitalizeWords = (str: string): string => {
    return str
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const breadcrumbs: BreadcrumbItem[] = pathSegments.map((segment, index) => {
    const path = '/' + pathSegments.slice(0, index + 1).join('/');
    const isLast = index === pathSegments.length - 1;
    return {
      name: isLast && currentPageName ? currentPageName : capitalizeWords(segment),
      path: path,
    };
  });

  if (breadcrumbs.length === 0) {
    return null;
  }

  return (
    <ol itemType="https://schema.org/BreadcrumbList" itemScope>
      <li itemProp="itemListElement" itemType="https://schema.org/ListItem" itemScope>
        <Link href="/" itemProp="item">
          <span itemProp="name">Home</span>
        </Link>
        <meta content="1" itemProp="position" />
      </li>
      {breadcrumbs.map((breadcrumb, index) => {
        const isLast = index === breadcrumbs.length - 1;
        return (
          <li
            aria-current={isLast ? 'page' : undefined}
            itemProp="itemListElement"
            itemType="https://schema.org/ListItem"
            key={breadcrumb.path}
            itemScope
          >
            <Link href={breadcrumb.path} itemProp="item">
              <span itemProp="name">{breadcrumb.name}</span>
            </Link>
            <meta content={`${index + 2}`} itemProp="position" />
          </li>
        );
      })}
    </ol>
  );
};

export default Breadcrumbs;
